import {
  Component, EventEmitter, Input, OnInit, OnDestroy,
  Output, ChangeDetectionStrategy, ChangeDetectorRef, inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import {
  NotificationService,
  IGroupedNotification,
  ISenderSnippet,
  NotificationType,
} from '../../../shared/services/notification.service';
import {
  FriendRequestService,
  IFriendRequestWithMutuals,
  IMutualFriendSnippet,
} from '../../../shared/services/friend-request.service';
import { CollabrationRequestService } from '../../../shared/services/collabration-request.service';

@Component({
  selector:        'app-notification',
  standalone:      true,
  imports:         [CommonModule, RouterModule],
  templateUrl:     './notification.html',
  styleUrl:        './notification.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Notification implements OnInit, OnDestroy {

  // ── Inputs / Outputs ────────────────────────────────────────────────────────

  @Input() set isOpen(value: boolean) {
    this._isOpen = value;
    if (value) { this.resetAndLoad(); }
  }
  get isOpen(): boolean { return this._isOpen; }
  private _isOpen = false;

  @Output() closed = new EventEmitter<void>();

  // ── DI ──────────────────────────────────────────────────────────────────────

  readonly notifService  = inject(NotificationService);
  private  friendService = inject(FriendRequestService);
  private  collabService = inject(CollabrationRequestService);
  readonly router        = inject(Router);
  private  cdr           = inject(ChangeDetectorRef);
  private  destroy$      = new Subject<void>();

  // ── State ───────────────────────────────────────────────────────────────────

  activeTab: 'notification' | 'friend' = 'notification';

  groups:           IGroupedNotification[]     = [];
  notifPage         = 1;
  notifLimit        = 20;
  notifTotal        = 0;
  notifLoading      = false;
  notifLoadingMore  = false;
  notifError        = '';

  friendReqs:    IFriendRequestWithMutuals[] = [];
  friendLoading  = false;
  friendError    = '';

  unreadCount    = 0;
  friendCount    = 0;

  expandedGroup: string | null             = null;
  actorModalGroup: IGroupedNotification | null = null;

  // ── Mutual friends popup state ───────────────────────────────────────────────

  mutualModalReq:      IFriendRequestWithMutuals | null = null;
  mutualModalFriends:  IMutualFriendSnippet[]           = [];
  mutualModalLoading   = false;

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.notifService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(c => { this.unreadCount = c; this.cdr.markForCheck(); });

    this.notifService.groups$
      .pipe(takeUntil(this.destroy$))
      .subscribe(g => { this.groups = g; this.cdr.markForCheck(); });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Load ─────────────────────────────────────────────────────────────────────

  private resetAndLoad(): void {
    this.notifPage  = 1;
    this.notifTotal = 0;
    this.notifError = '';
    this.loadNotifications(false);
    this.loadFriendRequests();
  }

  loadNotifications(append = false): void {
    if (append) { this.notifLoadingMore = true; }
    else        { this.notifLoading     = true;  }

    this.notifService.getGrouped(this.notifPage, this.notifLimit).subscribe({
      next: res => {
        const incoming  = res.data.groups;
        this.notifTotal = res.data.meta.total;

        if (append) {
          const existingKeys = new Set(this.groups.map(g => g.groupKey));
          const merged = [...this.groups, ...incoming.filter(g => !existingKeys.has(g.groupKey))];
          this.notifService.groups$.next(merged);
        } else {
          this.notifService.groups$.next(incoming);
        }

        this.notifService.unreadCount$.next(res.data.meta.unreadCount);
        this.notifLoading     = false;
        this.notifLoadingMore = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notifError       = 'Could not load notifications.';
        this.notifLoading     = false;
        this.notifLoadingMore = false;
        this.cdr.markForCheck();
      },
    });
  }

  loadMoreNotifications(): void {
    if (this.notifLoadingMore || this.groups.length >= this.notifTotal) return;
    this.notifPage++;
    this.loadNotifications(true);
  }

  get hasMoreNotifications(): boolean {
    return this.groups.length < this.notifTotal;
  }

  loadFriendRequests(): void {
    this.friendLoading = true;
    this.friendError   = '';
    this.friendService.getIncoming().subscribe({
      next: res => {
        this.friendReqs    = res.data ?? [];
        this.friendCount   = this.friendReqs.length;
        this.friendLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.friendError   = 'Could not load friend requests.';
        this.friendLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  // ── Notification actions ──────────────────────────────────────────────────────

  onGroupClick(group: IGroupedNotification): void {
    if (!group.isRead) {
      this.notifService.markGroupRead(group.notificationIds).subscribe();
      this.notifService.markGroupLocalRead(group.notificationIds);
    }

    if (group.actorCount > 1) {
      this.expandedGroup = this.expandedGroup === group.groupKey ? null : group.groupKey;
      this.cdr.markForCheck();
      return;
    }

    this.navigateForGroup(group);
    this.closePanel();
  }

  navigateForGroup(group: IGroupedNotification): void {
    const d    = group.data;
    const type = group.type;

    const contentId = (
      d['contentId'] ?? d['postId'] ?? d['blogId'] ?? d['eventId']
    ) as string | undefined;

    if (contentId) {
      // Comment notifications land directly on the comments tab/panel instead
      // of the default view, so the user sees the comment they were notified
      // about without an extra click.
      const extras = type.endsWith('_COMMENT') ? { queryParams: { openComments: '1' } } : undefined;
      if (type.startsWith('POST'))  { this.router.navigate(['/post',  contentId], extras); return; }
      if (type.startsWith('BLOG'))  { this.router.navigate(['/blog',  contentId], extras); return; }
      if (type.startsWith('EVENT')) { this.router.navigate(['/event', contentId], extras); return; }
    }

    if (
      type === 'FOLLOW' ||
      type === 'FRIEND_REQUEST' ||
      type === 'FRIEND_REQUEST_ACCEPTED' ||
      type === 'FRIEND_REQUEST_DECLINED'
    ) {
      this.router.navigate(['/profile', group.latestSender.userName, 'post']);
    }
  }

  openActorModal(group: IGroupedNotification, event: Event): void {
    event.stopPropagation();
    this.actorModalGroup = group;
    this.cdr.markForCheck();
  }

  closeActorModal(): void {
    this.actorModalGroup = null;
    this.cdr.markForCheck();
  }

  markAllRead(): void {
    this.notifService.markAllRead().subscribe();
    this.notifService.markAllLocalRead();
  }

  // ── Friend request actions ────────────────────────────────────────────────────

  acceptRequest(req: IFriendRequestWithMutuals): void {
    this.friendService.accept(req._id).subscribe({
      next: () => {
        this.friendReqs  = this.friendReqs.filter(r => r._id !== req._id);
        this.friendCount = this.friendReqs.length;
        this.cdr.markForCheck();
      },
    });
  }

  declineRequest(req: IFriendRequestWithMutuals): void {
    this.friendService.decline(req._id).subscribe({
      next: () => {
        this.friendReqs  = this.friendReqs.filter(r => r._id !== req._id);
        this.friendCount = this.friendReqs.length;
        this.cdr.markForCheck();
      },
    });
  }

  /** "Decline & Block" — declines the request and blocks the sender in one action. */
  declineAndBlockActioningId: string | null = null;

  declineAndBlockRequest(req: IFriendRequestWithMutuals): void {
    if (this.declineAndBlockActioningId) return;
    this.declineAndBlockActioningId = req._id;
    this.cdr.markForCheck();

    this.friendService.declineAndBlock(req._id).subscribe({
      next: () => {
        this.friendReqs  = this.friendReqs.filter(r => r._id !== req._id);
        this.friendCount = this.friendReqs.length;
        this.declineAndBlockActioningId = null;
        this.cdr.markForCheck();
      },
      error: () => {
        this.declineAndBlockActioningId = null;
        this.cdr.markForCheck();
      },
    });
  }

  // ── Mutual friends popup ──────────────────────────────────────────────────────

  /**
   * Opens the mutual-friends popup for the given friend request.
   * If the request already has all mutual friends loaded (from /incoming),
   * we show them directly. If somehow not populated, we fetch lazily.
   */
  openMutualModal(req: IFriendRequestWithMutuals, event: Event): void {
    event.stopPropagation();
    this.mutualModalReq     = req;
    this.mutualModalFriends = req.mutualFriends ?? [];

    // If we have them already, no need to fetch
    if (this.mutualModalFriends.length > 0) {
      this.mutualModalLoading = false;
      this.cdr.markForCheck();
      return;
    }

    // Lazy-fetch if for some reason the list is empty but count > 0
    this.mutualModalLoading = true;
    this.cdr.markForCheck();

    this.friendService.getMutualFriends(req.sender._id).subscribe({
      next: res => {
        this.mutualModalFriends = res.data ?? [];
        this.mutualModalLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.mutualModalLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  closeMutualModal(): void {
    this.mutualModalReq     = null;
    this.mutualModalFriends = [];
    this.mutualModalLoading = false;
    this.cdr.markForCheck();
  }

  /**
   * Returns the display label for mutual friends on the friend request card.
   * - 0 mutuals  → empty string (caller hides the row)
   * - 1 mutual   → "deven_sureja"
   * - 2 mutuals  → "deven_sureja and hit_baroda"
   * - 3+ mutuals → "deven_sureja and 2 others"
   */
  mutualLabel(req: IFriendRequestWithMutuals): string {
    const friends = req.mutualFriends ?? [];
    const total   = req.mutualFriendCount ?? friends.length;
    if (!total) return '';

    const first = friends[0]?.userName ?? '';
    if (total === 1) return first;
    if (total === 2) return `${first} and ${friends[1]?.userName ?? ''}`;
    return `${first} and ${total - 1} others`;
  }

  // ── Template helpers ──────────────────────────────────────────────────────────

  avatarUrl(sender: ISenderSnippet | IMutualFriendSnippet | any): string {
    return sender?.profileImage?.urlSmall
      ?? `https://ui-avatars.com/api/?name=${encodeURIComponent(sender?.displayName ?? 'U')}&background=FFD4E0&color=D31148`;
  }

  /** Content thumbnail (post/blog/event media) attached to like/comment/mention/collab notifications. */
  thumbnailUrl(group: IGroupedNotification): string | null {
    return (group.data?.['thumbnail'] as string) ?? null;
  }

  /** Short caption/content preview attached alongside the thumbnail, if any. */
  captionText(group: IGroupedNotification): string {
    return (group.data?.['caption'] as string) ?? '';
  }

  timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const s = Math.floor(diff / 1000);
    if (s < 60)  return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60)  return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24)  return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7)   return `${d}d`;
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  trackByGroupKey(_: number, g: IGroupedNotification)          { return g.groupKey; }
  trackByReqId(_: number,   r: IFriendRequestWithMutuals)      { return r._id; }
  trackBySenderId(_: number, s: ISenderSnippet)                 { return s._id; }
  trackByMutualId(_: number, m: IMutualFriendSnippet)           { return m._id; }

  // ── Collaboration request actions ─────────────────────────────────────────

  /** Track which notification groups are being actioned (accept/decline in progress) */
  collabActioningId: string | null = null;
  /** Track groups that have been acted on so UI updates immediately */
  collabActioned: Map<string, 'ACCEPTED' | 'DECLINED'> = new Map();

  isCollabNotification(group: IGroupedNotification): boolean {
    return group.type === 'POST_COLLABORATION' ||
           group.type === 'BLOG_COLLABORATION' ||
           group.type === 'EVENT_COLLABORATION';
  }

  getCollabActionStatus(group: IGroupedNotification): 'ACCEPTED' | 'DECLINED' | null {
    return this.collabActioned.get(group.groupKey) ?? null;
  }

  acceptCollab(group: IGroupedNotification, event: Event): void {
    event.stopPropagation();
    if (this.collabActioningId) return;
    const contentId = (
      group.data['contentId'] ?? group.data['postId'] ?? group.data['blogId'] ?? group.data['eventId']
    ) as string | undefined;
    if (!contentId) return;

    this.collabActioningId = group.groupKey;
    this.cdr.markForCheck();

    const obs = group.type === 'POST_COLLABORATION'
      ? this.collabService.postCollabrationAccept(contentId)
      : group.type === 'BLOG_COLLABORATION'
        ? this.collabService.blogCollabrationAccept(contentId)
        : this.collabService.eventCollabrationAccept(contentId);

    obs.subscribe({
      next: () => {
        this.collabActioned.set(group.groupKey, 'ACCEPTED');
        this.collabActioningId = null;
        if (!group.isRead) {
          this.notifService.markGroupRead(group.notificationIds).subscribe();
          this.notifService.markGroupLocalRead(group.notificationIds);
        }
        this.cdr.markForCheck();
      },
      error: () => { this.collabActioningId = null; this.cdr.markForCheck(); },
    });
  }

  declineCollab(group: IGroupedNotification, event: Event): void {
    event.stopPropagation();
    if (this.collabActioningId) return;
    const contentId = (
      group.data['contentId'] ?? group.data['postId'] ?? group.data['blogId'] ?? group.data['eventId']
    ) as string | undefined;
    if (!contentId) return;

    this.collabActioningId = group.groupKey;
    this.cdr.markForCheck();

    const obs = group.type === 'POST_COLLABORATION'
      ? this.collabService.postCollabrationDecline(contentId)
      : group.type === 'BLOG_COLLABORATION'
        ? this.collabService.blogCollabrationDecline(contentId)
        : this.collabService.eventCollabrationDecline(contentId);

    obs.subscribe({
      next: () => {
        this.collabActioned.set(group.groupKey, 'DECLINED');
        this.collabActioningId = null;
        if (!group.isRead) {
          this.notifService.markGroupRead(group.notificationIds).subscribe();
          this.notifService.markGroupLocalRead(group.notificationIds);
        }
        this.cdr.markForCheck();
      },
      error: () => { this.collabActioningId = null; this.cdr.markForCheck(); },
    });
  }

  closePanel(): void { this.closed.emit(); }
}